import { CalendarClockIcon, CircleAlertIcon } from 'lucide-react';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ReportScheduleInfo } from '@/lib/reports';
import { cn } from '@/lib/utils';
import { describeScheduleLocal, fmtClock, fmtInZone, FREQUENCY_LABEL, FREQUENCY_ORDER, ordinal, scheduleToCron, WEEKDAY_SHORT, type Frequency, type ScheduleModel } from './schedule';
import { TimeZonePicker, type ZoneSuggestion } from './TimeZonePicker';

interface Props {
  value: ScheduleModel;
  onChange: (m: ScheduleModel) => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  zoneSuggestions?: ZoneSuggestion[];
  /** Server explanation of the current cron + zone (plain words + next firings). */
  info?: ReportScheduleInfo | null;
  infoLoading?: boolean;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const MINUTES = [0, 5, 10, 15, 20, 30, 40, 45, 50];

/** Frequency → days → time → zone, with the resulting schedule explained and the next firings listed. Custom cron stays one click away. */
export function ScheduleBuilder({ value, onChange, timezone, onTimezoneChange, zoneSuggestions, info, infoLoading }: Props) {
  const set = (patch: Partial<ScheduleModel>) => onChange({ ...value, ...patch });
  const minutes = MINUTES.includes(value.minute) ? MINUTES : [...MINUTES, value.minute].sort((a, b) => a - b);
  const cron = scheduleToCron(value);
  const description = info?.valid ? info.description : null;
  const error = info && !info.valid ? info.error : null;

  return (
    <div className="space-y-5">
      <div>
        <Label className="mb-2 block">How often</Label>
        <div role="radiogroup" aria-label="Frequency" className="inline-flex flex-wrap border">
          {FREQUENCY_ORDER.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={value.frequency === f}
              onClick={() => set({ frequency: f, ...(f === 'custom' ? { cron } : {}) })}
              className={cn('px-3 py-1.5 text-sm transition-colors', value.frequency === f ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}
            >
              {FREQUENCY_LABEL[f as Frequency]}
            </button>
          ))}
        </div>
      </div>

      {value.frequency === 'weekly' && (
        <div>
          <Label className="mb-2 block">
            On these days <span className="text-muted-foreground font-normal">(pick one or more)</span>
          </Label>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Weekdays">
            {[1, 2, 3, 4, 5, 6, 0].map((d) => {
              const on = value.weekdays.includes(d);
              return (
                <button key={d} type="button" aria-pressed={on} onClick={() => set({ weekdays: on ? value.weekdays.filter((x) => x !== d) : [...value.weekdays, d] })} className={cn('min-w-12 border px-2.5 py-1.5 text-sm transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent')}>
                  {WEEKDAY_SHORT[d]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value.frequency === 'monthly' && (
        <div>
          <Label className="mb-2 block">
            On these dates <span className="text-muted-foreground font-normal">(months without the date are skipped)</span>
          </Label>
          <div className="grid w-fit grid-cols-7 gap-1" role="group" aria-label="Days of the month">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
              const on = value.monthDays.includes(d);
              return (
                <button key={d} type="button" aria-pressed={on} aria-label={ordinal(d)} onClick={() => set({ monthDays: on ? value.monthDays.filter((x) => x !== d) : [...value.monthDays, d] })} className={cn('size-9 border text-sm tabular-nums transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent', d > 28 && !on && 'text-muted-foreground')}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value.frequency === 'custom' ? (
        <Field label="Cron expression" htmlFor="rp-cron" description="minute · hour · day of month · month · weekday, evaluated in the time zone below. Names (mon-fri, jan) and steps (*/15) work.">
          <Input id="rp-cron" value={value.cron} onChange={(e) => set({ cron: e.target.value })} className="font-mono" placeholder="0 7 * * 1-5" spellCheck={false} />
        </Field>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[auto_auto_1fr] sm:items-end">
          {value.frequency !== 'hourly' && (
            <Field label="At" htmlFor="rp-hour">
              <div className="flex items-center gap-1.5">
                <Select value={String(value.hour)} onValueChange={(v) => set({ hour: Number(v) })}>
                  <SelectTrigger id="rp-hour" className="w-28" aria-label="Hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {fmtClock(h, 0).replace(':00', '')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground">:</span>
                <Select value={String(value.minute)} onValueChange={(v) => set({ minute: Number(v) })}>
                  <SelectTrigger className="w-20" aria-label="Minute">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {minutes.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {String(m).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Field>
          )}
          {value.frequency === 'hourly' && (
            <Field label="At minute" htmlFor="rp-minute">
              <Select value={String(value.minute)} onValueChange={(v) => set({ minute: Number(v) })}>
                <SelectTrigger id="rp-minute" className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {minutes.map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      :{String(m).padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <div className="hidden sm:block" />
          <Field label="Time zone" htmlFor="rp-tz" className="sm:col-start-3">
            <TimeZonePicker id="rp-tz" value={timezone} onChange={onTimezoneChange} suggestions={zoneSuggestions} />
          </Field>
        </div>
      )}
      {value.frequency === 'custom' && (
        <Field label="Time zone" htmlFor="rp-tz">
          <TimeZonePicker id="rp-tz" value={timezone} onChange={onTimezoneChange} suggestions={zoneSuggestions} />
        </Field>
      )}

      <div className={cn('flex flex-col gap-2 border p-3 text-sm sm:flex-row sm:items-start sm:justify-between', error ? 'border-destructive/40 bg-destructive/5' : 'bg-muted/40')} aria-live="polite">
        <div className="flex items-start gap-2">
          {error ? <CircleAlertIcon className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden /> : <CalendarClockIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />}
          <div>
            <div className={cn('font-medium', error && 'text-destructive')}>{error ?? description ?? describeScheduleLocal(value)}</div>
            <div className="text-muted-foreground text-xs">
              {timezone}
              {value.frequency !== 'custom' && <span className="font-mono"> · {cron}</span>}
            </div>
          </div>
        </div>
        {!error && (
          <div className="text-muted-foreground shrink-0 text-xs sm:text-right">
            <div className="text-foreground/80 font-medium">Next runs</div>
            {infoLoading && !info ? <div>Checking…</div> : info?.nextRuns.length ? info.nextRuns.slice(0, 3).map((n) => <div key={n}>{fmtInZone(n, timezone)}</div>) : <div>—</div>}
          </div>
        )}
      </div>
    </div>
  );
}
